import React, {ReactElement} from "react";
import {PathComponent} from "index";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "@/shared/BaseFragment";
import {StyledBoxRow, StyledSpan} from "@/shared/StyledComponents";
import {DIVIDER, PAGE_FRAGMENT_XXXLG_WIDTH, PD_MD, PD_SM, SZ_SM} from "@/shared/dimens";
import {ButtonBase, Card} from "@mui/material";
import {UserCache, UserProfilePhoto} from "@/shared/types";
import {UserFragment} from "@/shared/UserFragment";
import {Auth, getAuth} from "@firebase/auth";
import {User} from "@/shared/entities";
import {PathProps} from "@/shared";
import {BaseApp} from "../shared/BaseApp";
import {HomeOutlined, TableRowsOutlined} from "@mui/icons-material";
import {AppTabsContainer} from "../shared/AppTabsContainer";
import {Tabs, TabsColorScheme, TabsContainer} from "../shared/TabsContainer";
import {DashboardFragment} from "./dashboard/DashboardFragment";
import {ActivityLogsFragment} from "./activitylogs/ActivityLogsFragment";
import {requiresProject} from "../fyneai-shared/BaseModuleMain";
import {ResourceSelect} from "../fyneai-shared/ResourceSelect";

enum Tab {
  DASHBOARD = "dashboard",
  ACTIVITY = "activity",
}

const TABS: Tabs<Tab> = {
  items: [
    {
      type: Tab.DASHBOARD,
      path: "dashboard",
      text: "Dashboard",
      iconType: HomeOutlined,
      render: requiresProject(path => <DashboardFragment path={path}/>),
    },
    {
      type: Tab.ACTIVITY,
      path: "activity",
      text: "Activity",
      iconType: TableRowsOutlined,
      render: requiresProject(path => <ActivityLogsFragment path={path}/>),
    },
  ],
  containerId: "main",
}

function MainTitleBar(props: {
  auth?: Auth,
  path: PathProps,
}): ReactElement {
  const colorScheme = BaseApp.CONTEXT.getPreferredColorScheme();
  return <StyledBoxRow style={{
    flexGrow: 1,
    maxWidth: PAGE_FRAGMENT_XXXLG_WIDTH,
    alignItems: "center",
  }}>
    <a href={"/"}>
      <StyledBoxRow style={{alignItems: "center"}}>
        <img src={"/images/fyneai_logotype.svg"}
             style={{height: 32, filter: (colorScheme === "dark" ? "invert(1)" : null)}}/>
      </StyledBoxRow>
    </a>
    <StyledSpan/>
    <ResourceSelect/>
    <StyledBoxRow style={{gap: 4, alignItems: "center"}}>
      <Card style={{marginLeft: PD_SM, width: SZ_SM, height: SZ_SM, flexShrink: 0}}>
        <ButtonBase
          onClick={() => BaseApp.CONTEXT.showDialog(
            null,
            () => <UserFragment uid={props.auth.currentUser.uid}/>)}>
          <img src={UserProfilePhoto(UserCache.getInstance().getCachedUser(props.auth.currentUser.uid))}
               style={{width: "100%", height: "100%"}}/>
        </ButtonBase>
      </Card>
    </StyledBoxRow>
  </StyledBoxRow>;
}

export type MainProps = BaseFragmentProps & {}

type MainState = BaseFragmentState & {
  user?: User,
}

export class Main extends BaseFragment<MainProps, MainState> {

  private readonly auth = getAuth();

  static nestedPaths(): PathComponent[] {
    return TabsContainer.nestedPathsFromTabs(TABS);
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    const user = await UserCache.getInstance().getUser(this.auth.currentUser.uid);
    this.setState({
      user,
    });
  }

  protected renderContainerContent(): React.ReactElement | null {
    const tabsColorScheme: TabsColorScheme = BaseApp.CONTEXT.getPreferredColorScheme() || "light";
    return <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100dvw",
        height: "100dvh",
        overflow: "hidden",
      }}>
      <StyledBoxRow style={{
        padding: PD_MD,
        alignItems: "center",
        height: 72,
        justifyContent: "center",
        background: this.theme.palette.background.default,
        borderBottom: DIVIDER,
      }}>
        <MainTitleBar
          auth={this.auth}
          path={this.props.path}/>
      </StyledBoxRow>
      <AppTabsContainer
        path={this.props.path}
        noLogo={true}
        noUserButton={true}
        tabs={TABS}
        tabsThemeColor={this.theme.palette.grey[tabsColorScheme as string === "light" ? 100 : 900]}
        tabsColorScheme={tabsColorScheme}
      />
    </div>;
  }
}